import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { ALL_SCENES } from './scenes';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_INDEX = "https://customer-assets.emergentagent.com/job_latency-zero-dsp/artifacts/pi9quyh5_Index.png";
const LOGO_BEDROCK = "https://customer-assets.emergentagent.com/job_latency-zero-dsp/artifacts/z3igixfs_BedrockPlatform-Logo-FontVersion-BW-72dpi.jpeg";

const SCENES = [
  {
    title: "The Programmatic Ecosystem",
    subtitle: "Millions of ad opportunities every second",
    voiceover: "Every second, millions of ad opportunities flow through supply-side platforms — from publishers, websites, apps, and live streaming services.",
  },
  {
    title: "Traditional DSP Architecture",
    subtitle: "External bidding introduces latency and data loss",
    voiceover: "Traditional DSPs operate outside the SSP infrastructure. Bid requests travel across networks, introducing latency and inefficiencies. Some bid requests are arbitrarily dropped — for example when certain parameters, like a specific user identifier field, are not passed in the bid request.",
  },
  {
    title: "The Traffic Shaping Problem",
    subtitle: "Algorithms gate valuable inventory",
    voiceover: "To manage scale, external DSPs use traffic shaping algorithms — dropping or ignoring large portions of available inventory. This can happen randomly, or due to a conflict of interest where the DSP drops bids on inventory it knows may not be profitable.",
  },
  {
    title: "Missed Opportunities",
    subtitle: "Premium deals go unbid",
    voiceover: "The result is missed opportunities across premium inventory and private marketplace deals. Campaigns never get a chance to bid on traffic they know to be valuable or performant.",
  },
  {
    title: "DSP Running Inside SSP",
    subtitle: "Zero network travel. Instant access.",
    voiceover: "Containerizing the Bedrock DSP inside the Index Exchange SSP removes network delays and means instant access and reaction to all available bids.",
  },
  {
    title: "100% Deal Visibility",
    subtitle: "Every impression evaluated, no exceptions",
    voiceover: "The containerized DSP sees and evaluates 100% of impressions available in private deals.",
  },
  {
    title: "Ultra-Low Latency",
    subtitle: "From over 100ms to under 1ms",
    voiceover: "Bid response latency drops from over one hundred milliseconds to under one millisecond.",
  },
  {
    title: "Live Streaming Advantage",
    subtitle: "Winning in the fastest real-time auctions",
    voiceover: "This dramatic latency reduction is critical for live streaming inventory, where auction windows are extremely short and competition is high.",
  },
  {
    title: "Higher Win Rates",
    subtitle: "Measurably better campaign performance",
    voiceover: "With faster bidding and full deal visibility, campaigns achieve significantly higher win rates and far fewer dropped bids.",
  },
  {
    title: "Stable Campaign Delivery",
    subtitle: "Smooth, predictable pacing",
    voiceover: "The result is more stable campaign delivery across premium programmatic and live streaming environments.",
  },
  {
    title: "The Full Potential of Programmatic",
    subtitle: "Bedrock DSP × Index Exchange",
    voiceover: "Containerizing the Bedrock DSP inside SSP infrastructure unlocks the full potential of programmatic buying.",
  },
];

function App() {
  const [currentScene, setCurrentScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioCacheRef = useRef({});
  const audioRef = useRef(null);
  const cancelRef = useRef(false);

  const fetchAudio = useCallback(async (idx) => {
    if (audioCacheRef.current[idx]) return audioCacheRef.current[idx];
    setLoadingAudio(true);
    try {
      const res = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: SCENES[idx].voiceover, voice: 'onyx', model: 'tts-1-hd' }),
      });
      if (!res.ok) throw new Error('TTS failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      audioCacheRef.current[idx] = url;
      return url;
    } catch (e) {
      console.error('TTS error for scene', idx, e);
      return null;
    } finally {
      setLoadingAudio(false);
    }
  }, []);

  // Auto-play + TTS sync
  useEffect(() => {
    if (!isPlaying) return;
    cancelRef.current = false;

    const run = async () => {
      const url = await fetchAudio(currentScene);
      if (cancelRef.current) return;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
      }

      if (!url) {
        // No audio: auto-advance after 6 seconds
        const t = setTimeout(() => {
          if (!cancelRef.current) {
            if (currentScene < SCENES.length - 1) setCurrentScene(p => p + 1);
            else setIsPlaying(false);
          }
        }, 6000);
        return () => clearTimeout(t);
      }

      const audio = new Audio(url);
      audio.muted = isMuted;
      audioRef.current = audio;
      audio.onended = () => {
        if (!cancelRef.current) {
          setTimeout(() => {
            if (currentScene < SCENES.length - 1) setCurrentScene(p => p + 1);
            else setIsPlaying(false);
          }, 600);
        }
      };
      audio.play().catch(console.error);

      // Pre-load next scene
      if (currentScene < SCENES.length - 1) fetchAudio(currentScene + 1);
    };

    run();
    return () => {
      cancelRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
      }
    };
  }, [currentScene, isPlaying, fetchAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = isMuted;
  }, [isMuted]);

  const stopAudio = () => {
    cancelRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current = null;
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => { setIsPlaying(false); stopAudio(); };
  const handleNext = () => {
    if (currentScene < SCENES.length - 1) { stopAudio(); setCurrentScene(p => p + 1); }
  };
  const handlePrev = () => {
    if (currentScene > 0) { stopAudio(); setCurrentScene(p => p - 1); }
  };
  const handleSceneClick = (idx) => {
    stopAudio();
    setCurrentScene(idx);
    if (isPlaying) setIsPlaying(true); // re-triggers effect
  };

  const SceneComponent = ALL_SCENES[currentScene];

  return (
    <div className="app-wrapper">
      {/* Header */}
      <header className="app-header">
        <div className="header-logos">
          <div className="logo-wrap">
            <img src={LOGO_BEDROCK} alt="Bedrock Platform" />
          </div>
          <span className="logo-sep">×</span>
          <div className="logo-wrap">
            <img src={LOGO_INDEX} alt="Index Exchange" />
          </div>
        </div>
        <h1 className="app-title">Containerized DSP Inside SSP Infrastructure</h1>
        <div className="header-right">
          <button
            className="ctrl-btn"
            onClick={() => setIsMuted(m => !m)}
            title={isMuted ? 'Unmute' : 'Mute'}
            data-testid="mute-btn"
            style={{ padding: '6px 12px' }}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>
      </header>

      {/* Animation Stage */}
      <div className="stage-wrapper">
        <div className="scene-stage">
          <div className="scene-inner" key={currentScene}>
            <SceneComponent indexLogo={LOGO_INDEX} bedrockLogo={LOGO_BEDROCK} />

            {/* Scene title overlay */}
            <div className="scene-title-bar">
              <div className="scene-num">SCENE {currentScene + 1} / {SCENES.length}</div>
              <div className="scene-title-text">{SCENES[currentScene].title}</div>
              <div className="scene-subtitle-text">{SCENES[currentScene].subtitle}</div>
            </div>

            {/* Audio loading indicator */}
            {loadingAudio && isPlaying && (
              <div className="audio-badge">
                <span className="loading-dot" />
                Generating voiceover...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        {/* Timeline */}
        <div className="timeline" data-testid="scene-progress-bar">
          {SCENES.map((_, i) => (
            <button
              key={i}
              className={`timeline-dot ${i === currentScene ? 'active' : ''} ${i < currentScene ? 'done' : ''}`}
              onClick={() => handleSceneClick(i)}
              title={SCENES[i].title}
              data-testid={`timeline-dot-${i}`}
            />
          ))}
        </div>

        <div className="controls-row">
          <button
            className="ctrl-btn"
            onClick={handlePrev}
            disabled={currentScene === 0}
            data-testid="prev-scene-btn"
          >
            <SkipBack size={16} />
            Prev
          </button>

          {isPlaying ? (
            <button
              className="ctrl-btn primary"
              onClick={handlePause}
              data-testid="play-pause-btn"
            >
              <Pause size={16} />
              Pause
            </button>
          ) : (
            <button
              className="ctrl-btn primary"
              onClick={handlePlay}
              data-testid="play-pause-btn"
            >
              <Play size={16} />
              {currentScene === 0 ? 'Play' : 'Resume'}
            </button>
          )}

          <button
            className="ctrl-btn"
            onClick={handleNext}
            disabled={currentScene === SCENES.length - 1}
            data-testid="next-scene-btn"
          >
            Next
            <SkipForward size={16} />
          </button>
        </div>

        <div className="scene-label-row">
          <span className="scene-counter-text">
            Scene {currentScene + 1} — {SCENES[currentScene].title}
          </span>
          <span className="voiceover-hint">
            {isPlaying ? '▶ Voiceover playing' : '● Click Play to start with voiceover'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default App;
